/* ===================================================================
   Minimal, dependency-free CSV parser for the Customers import (Hopper,
   2026-06-17).

   Handles the common cases a small-business owner's export throws at it:
   quoted fields, embedded commas inside quotes, escaped double-quotes (""),
   CRLF or LF line endings, and a header row. It is intentionally small --
   not a full RFC-4180 implementation -- but robust enough for name/email/
   phone contact lists. Header names are matched flexibly (first name / "First
   Name" / firstname all map to first_name).
   =================================================================== */
import type { CustomerInput } from './api';

/* Tokenize one CSV file into an array of string-cell rows. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  // Strip a leading BOM if present.
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++; // skip the escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (ch === '\r') {
      // handled by the \n branch; ignore a lone CR
    } else {
      cell += ch;
    }
  }
  // Flush the trailing cell/row (file without a final newline).
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  // Drop fully-empty rows.
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/* Normalize a header label to a canonical key. */
function headerKey(h: string): string | null {
  const k = h.trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (['firstname', 'first', 'fname', 'givenname'] .includes(k)) return 'first_name';
  if (['lastname', 'last', 'lname', 'surname', 'familyname'].includes(k)) return 'last_name';
  if (['name', 'fullname', 'customer', 'customername', 'contact'].includes(k)) return 'name';
  if (['email', 'emailaddress', 'e-mail', 'mail'].includes(k)) return 'email';
  if (['phone', 'phonenumber', 'mobile', 'cell', 'tel', 'telephone'].includes(k)) return 'phone';
  if (['notes', 'note', 'comment', 'comments'].includes(k)) return 'notes';
  // Job-completed date (the recommended template's 4th column). The backend
  // customer record has no dedicated column for it (adding one would need a
  // migration), so it is recognized here -- so the template validates and the
  // value is not treated as junk -- and folded into `notes` at row build time.
  if (
    [
      'jobcompleteddate', 'jobdate', 'jobcompleted', 'completeddate',
      'datecompleted', 'servicedate', 'dateofservice', 'lastservice',
      'lastservicedate',
    ].includes(k)
  )
    return 'job_completed_date';
  return null;
}

export interface CsvParseResult {
  rows: CustomerInput[];
  /** Header keys we recognized (canonical). */
  recognized: string[];
  /** Total non-empty data rows seen (before contact-channel filtering). */
  totalRows: number;
}

/* Parse a CSV file's text into CustomerInput rows, mapping header columns.
   Rows with neither email nor phone are dropped here (the server also guards
   this) so the import count is honest. */
export function parseCustomersCsv(text: string): CsvParseResult {
  const cells = parseCsv(text);
  if (cells.length === 0) return { rows: [], recognized: [], totalRows: 0 };

  const header = cells[0].map(headerKey);
  const recognized = header.filter((h): h is string => h != null);
  // If no header was recognized at all, assume positional: name, email, phone.
  const hasHeader = recognized.length > 0;
  const dataRows = hasHeader ? cells.slice(1) : cells;

  const out: CustomerInput[] = [];
  for (const r of dataRows) {
    const rec: CustomerInput = {};
    if (hasHeader) {
      let jobDate = '';
      header.forEach((key, idx) => {
        if (!key) return;
        const v = (r[idx] ?? '').trim();
        if (!v) return;
        // job_completed_date has no backend field -> capture, don't send it as
        // a stray key. Folded into notes below (only when notes is empty, so a
        // real note is never clobbered).
        if (key === 'job_completed_date') {
          jobDate = v;
          return;
        }
        (rec as Record<string, string>)[key] = v;
      });
      if (jobDate && !rec.notes) rec.notes = `Job completed: ${jobDate}`;
    } else {
      // positional fallback: col0=name, col1=email, col2=phone
      const [n, e, p] = r;
      if ((n ?? '').trim()) rec.name = n.trim();
      if ((e ?? '').trim()) rec.email = e.trim();
      if ((p ?? '').trim()) rec.phone = p.trim();
    }
    out.push(rec);
  }
  const filtered = out.filter((r) => (r.email && r.email.trim()) || (r.phone && r.phone.trim()));
  return { rows: filtered, recognized, totalRows: out.length };
}
